import ReactTooltip from "react-tooltip";

import { ReactComponent as Pin } from "assets/pin 1.svg";
import { ReactComponent as Car } from "assets/car.svg";
// import { ReactComponent as History } from "assets/history.svg";
import { ReactComponent as Home } from "assets/home.svg";
import { ReactComponent as AddressBook } from "assets/SidebarIcons/addressbook.svg";
import { ReactComponent as PinIcon } from "assets/local.svg";
import { ReactComponent as StopwatchIcon } from "assets/stopwatch.svg";
import { ReactComponent as MarkedLocal } from "assets/marked-local.svg";
import { ReactComponent as RoadSign } from "assets/road-sign.svg";
import { ReactComponent as CopyOne } from "assets/copy-one.svg";

import { Skeleton } from "components/Skeleton";
import * as S from "./styles";

import OccurrenceManagementModal from "../../Modals/OccurrenceManagementModal";
import { IOccurrence } from "interfaces/Occurrence";
import { useMonitoringCard } from "./useMonitoringCard";
import { CardTimer, ColumnHeader, InfoText, Options, AttendantBadge, PolicemanSummary, ReportSummary } from "./components";
import { IReport } from "interfaces/Report";

type CardProps = {
  occurrence: IOccurrence;
  reportClick: () => void;
  commentsClick: () => void;
  cancelClick: () => void;
  attachmentsClick: (report?: IReport) => void;
  priorizedCard: (isFavorite: boolean, occurrenceId: string) => void;
  isFavorite?: boolean;
};

export function MonitoringCard({
  occurrence,
  commentsClick,
  cancelClick,
  attachmentsClick,
  priorizedCard,
  isFavorite,
}: CardProps) {
  const {
    isPriorized,
    isLate,
    notPr,
    isCompleted,
    isCountdownLoading,
    isWaiting,
    occurrenceElapsedTime,
    idleStatus,
    isEditOccurrenceManagementModalOpen,
    occurrenceType,
    handleOccurrenceEdit,
    handleIsPriorized,
    formatProtocol,
    copyProtocol,
    getAddress,
    getCurrentIdleStatus,
    setIsEditOccurrenceManagementModalOpen,
    copyShareLink
  } = useMonitoringCard({ occurrence, priorizedCard, isFavorite })

  if (!occurrence.id) return <></>;

  return (
    <>
      <S.CardWrapper data-cy="monitoring-card">
        <div className="container">
          <S.CardHeader>
            <S.PinButton
              isActive={isPriorized || Boolean(isFavorite)}
              onClick={() => handleIsPriorized(occurrence.id)}
            >
              <Pin />
            </S.PinButton>

            {isLate ? (
              <S.StatusBorder
                variants="isLate"
                data-tip="O Pronta-resposta está atrasado"
              >
                <S.StyledReactTooltip />
              </S.StatusBorder>
            ) : notPr && !isCompleted ? (
              <S.StatusBorder
                variants="notPr"
                data-tip="Nenhum pronta-resposta associado para o trabalho"
              >
                <S.StyledReactTooltip />
              </S.StatusBorder>
            ) : occurrence.status === "aguardando_cancelamento" ? (
              <S.StatusBorder
                variants="isCancelled"
                data-tip="Aguardando confirmação do cancelamento do pronta-resposta"
              >
                <S.StyledReactTooltip />
              </S.StatusBorder>
            ) : (
              <S.StatusBorder data-tip="Em andamento">
                <S.StyledReactTooltip />
              </S.StatusBorder>
            )}
          </S.CardHeader>

          <S.ProtocolContainer>
            <div>
              {occurrence.isManual && (
                <S.ManualBadgeContainer>
                  <span>Ocorrência manual</span>
                </S.ManualBadgeContainer>
              )}

              <div className="header">
                <div className="occurrence-icon">
                  {occurrence.patrimonialType === 2 ? <Car /> : <Home />}
                </div>
                <div>
                  <p>Ocorrência</p>
                  <span>
                    {occurrence.patrimonialType === 2
                      ? "Veicular"
                      : "Patrimonial"}
                  </span>
                </div>
              </div>

              <div className="body">
                {occurrence.status === 'aguardando_cancelamento' && (
                  <S.Badge className="aguardando_cancelamento">
                    <p style={{ fontSize: 13, textAlign: 'center' }}>
                      Aguardando cancelamento pelo aplicativo
                    </p>
                  </S.Badge>
                )}

                <div className="protocol-id-container">
                  <InfoText
                    label="Protocolo"
                    data={
                      occurrence.protocol
                        ? String(occurrence.protocol)
                        : formatProtocol(occurrence.id)
                    }
                    isLarge
                  />
                  <button onClick={() => copyProtocol()}>
                    <CopyOne />
                  </button>
                </div>


                <PolicemanSummary reports={occurrence.report} />
              </div>
            </div>

            <AttendantBadge name={occurrence.attendant.name} />
          </S.ProtocolContainer>

          <div className="column">
            <div>
              <ColumnHeader
                icon={<AddressBook />}
                title="Cliente e beneficiário"
              />

              <article>
                <InfoText label="Cliente" data={occurrence?.client?.name} />

                {occurrence?.occurrencesSuperficialAddresses.superficialAddress
                  ?.superficialClient?.name && (
                    <InfoText
                      label="Beneficiário"
                      data={
                        occurrence?.occurrencesSuperficialAddresses
                          .superficialAddress?.superficialClient?.name
                      }
                    />
                  )}
              </article>
            </div>

            <AttendantBadge name={occurrence.attendant.name} />
          </div>

          {occurrence.patrimonialType === 2 && (
            <div className="column">
              <div>
                <ColumnHeader icon={<Car />} title="Veículo" />

                <article>
                  <InfoText
                    label="Placa"
                    data={occurrence.superficialVehicle?.plate || "--"}
                  />
                  <InfoText
                    label="Modelo"
                    data={occurrence.superficialVehicle?.model || "--"}
                  />
                  <InfoText
                    label="Marca"
                    data={occurrence.superficialVehicle?.brand || "--"}
                  />
                </article>
              </div>

              <AttendantBadge name={occurrence.attendant.name} />
            </div>
          )}

          <div className="column">
            <div>
              <ColumnHeader icon={<PinIcon />} title="Endereço" />

              <article>
                <InfoText
                  icon={<MarkedLocal />}
                  data={getAddress(
                    occurrence?.occurrencesSuperficialAddresses
                      .superficialAddress.street,
                    occurrence?.occurrencesSuperficialAddresses
                      .superficialAddress.number
                  )}
                />

                {occurrence.patrimonialType === 2 && occurrence.lastCoordAddress && (
                  <InfoText
                    label="Última vez visto em"
                    icon={<RoadSign />}
                    data={occurrence.lastCoordAddress}
                  />
                )}
              </article>
            </div>

            <AttendantBadge name={occurrence.attendant.name} />
          </div>

          <div className="column">
            <div style={{ width: '100%' }}>
              <ColumnHeader
                icon={<StopwatchIcon />}
                title="Informações adicionais"
              />

              {isCountdownLoading && (
                <Skeleton width="100%" height={100} style={{ borderRadius: 6, marginTop: '1.5rem' }} />
              )}

              <article>
                <ReportSummary
                  reports={occurrence.report}
                  attachmentsClick={attachmentsClick}
                />

                {/* <InfoText icon={<History />} data={timeRemaining} /> */}

                <CardTimer
                  isWaiting={isWaiting}
                  currentElapsedTime={occurrenceElapsedTime}
                />

                {idleStatus &&
                  !occurrence.policemen[0] &&
                  !occurrence.isManual && (
                    <S.IdleContainer isStopped={idleStatus === "stopped"}>
                      {getCurrentIdleStatus()}
                    </S.IdleContainer>
                  )}
              </article>
            </div>

            <AttendantBadge name={occurrence.attendant.name} />
          </div>

          <S.ActionsContainer className="column">
            <Options
              attachmentsClick={attachmentsClick}
              cancelClick={cancelClick}
              commentsClick={commentsClick}
              copyShareLink={copyShareLink}
              handleOccurrenceEdit={handleOccurrenceEdit}
              occurrence={occurrence}
              priorizedCard={priorizedCard}
            />
          </S.ActionsContainer>
        </div>

        <S.Footer>
          <Options
            attachmentsClick={attachmentsClick}
            cancelClick={cancelClick}
            commentsClick={commentsClick}
            copyShareLink={copyShareLink}
            handleOccurrenceEdit={handleOccurrenceEdit}
            occurrence={occurrence}
            priorizedCard={priorizedCard}
          />
        </S.Footer>

        <ReactTooltip
          id="occurrenceCardTooltip"
          place="top"
          type="dark"
          effect="solid"
          className="tooltip"
          backgroundColor={"var(--grey-300)"}
          html
        />
      </S.CardWrapper>

      <OccurrenceManagementModal
        type={occurrenceType}
        isOpen={isEditOccurrenceManagementModalOpen}
        setIsOpen={() => setIsEditOccurrenceManagementModalOpen(false)}
      />
    </>
  );
}
